/**
 * Enhanced Tenant Configuration Types
 * For dynamic module integrations with feature flags
 */

// Feature Flag Interface
export interface FeatureFlag {
  displayName: string;
  enabled: boolean;
  enabledOn: string;
  moduleName: string;
  configured: boolean;
}

// Module Configuration Interface
export interface ModuleConfig {
  [key: string]: any;
  settingsComponent?: string;
  healthCheckEndpoint?: string;
}

export const CLIO_REPLICATION_SCHEDULER_ENTITIES = [
  'clio-users',
  'clio-contacts',
  'clio-matters',
  'clio-activities',
  'clio-wip',
  'clio-activity-descriptions',
  'clio-expense-categories',
  'clio-utbms-codes',
  'clio-utbms-sets',
  'clio-bank-accounts',
  'clio-bank-transactions',
  'clio-bills',
  'clio-line-items',
  'clio-allocations',
  'clio-groups',
  'clio-credit-memos',
  'clio-trust-line-items',
  'clio-activity-rates',
  'clio-custom-field-sets',
  'clio-custom-fields',
  'clio-currencies',
  'clio-matter-stages',
  'juniper-eligible-matter-view',
] as const;

export type ClioReplicationSchedulerEntity = typeof CLIO_REPLICATION_SCHEDULER_ENTITIES[number];
export type ReplicateToDWConditionType = 'startDate' | 'rollingMonthsWindow';
export type ClioReportPeriodModel = 'CALENDAR' | 'FISCAL';
export type ClioFiscalPattern = '4-4-5' | '4-5-4' | '5-4-4';
export type ClioWeekStartDay = 'MONDAY' | 'SUNDAY';
export type ClioWeek53Handling = 'P12' | 'P11' | 'nearest-end';
export type ClioBackfillDefaultPeriods = 12 | 24 | 36;

export const CLIO_REPLICATION_REPORT_KINDS = [
  'accounts_receivable',
  'accounts_receivable_aging',
  'fee_allocation',
  'matter',
  'matter_balance_summary',
  'revenue',
  'trust_management',
] as const;

export type ClioReplicationReportKind = typeof CLIO_REPLICATION_REPORT_KINDS[number];

export interface ClioReplicationSchedulerEntityConfig {
  replicateToClio: boolean;
  replicateToDW: boolean;
  replicateToDWConditionType?: ReplicateToDWConditionType;
  replicateToDWValue?: string | number;
}

export type ClioReplicationSchedulerEntityMap = Record<ClioReplicationSchedulerEntity, ClioReplicationSchedulerEntityConfig>;

export interface ClioReplicationReportReplicationConfig {
  enabled: boolean;
  periodModel: ClioReportPeriodModel;
  fiscalPattern: ClioFiscalPattern;
  weekStartDay: ClioWeekStartDay;
  fiscalYearAnchorDate: string;
  week53Handling: ClioWeek53Handling;
  finalizeClosedPeriods: boolean;
  backfillDefaultPeriods: ClioBackfillDefaultPeriods;
  reports: Record<ClioReplicationReportKind, boolean>;
  /** Per-report toggle: when true (default), extract by monthly period with date filters. When false, extract full snapshot with no date filters and TRUNCATE+COPY in Redshift. */
  reportExportByMonth: Record<ClioReplicationReportKind, boolean>;
}

export interface ClioReplicationSchedulerConfig extends ModuleConfig {
  enabled: boolean;
  entities: ClioReplicationSchedulerEntityMap;
  reportReplication: ClioReplicationReportReplicationConfig;
  settingsComponent?: 'DataReplicationConfigurationCard';
  healthCheckEndpoint?: '/health/clio-replication-scheduler';
}

// Clio Module Configuration
export interface ClioModuleConfig extends ModuleConfig {
  redirectUri: string;
  baseUrl: string;
  appId?: string;
  clientId?: string;
  clientSecret?: {
    ssmPath: string;
  };
  authorizationCode?: {
    ssmPath: string;
  };
  webhooksConfigured: boolean;
  webhookSubscriptions?: ClioWebhookSubscription[];
  webhookUrls: {
    billCreated: string;
    billUpdated: string;
    billDeleted: string;
  };
  webhookUrl?: string;
  clioWebHookUri: string;
  settingsComponent: 'ClioConfigModal';
  healthCheckEndpoint: '/health/clioAPI';
}

// QBO Module Configuration
export interface QboModuleConfig extends ModuleConfig {
  redirectUri: string;
  baseUrl: string;
  appId?: string;
  clientId?: string;
  clientSecret?: {
    ssmPath: string;
  };
  accessToken?: {
    ssmPath: string;
  };
  refreshToken?: {
    ssmPath: string;
  };
  realmId?: string;
  webhooksConfigured: boolean;
  webhookSubscriptions?: QboWebhookSubscription[];
  webhookUrl?: string;
  qboWebhookUri: string;
  settingsComponent: 'QBOConfigModal';
  healthCheckEndpoint: '/health/qboAPI';
}

// Email Notifications Module Configuration (legacy - now part of TenantSettingsModuleConfig)
export interface EmailModuleConfig extends ModuleConfig {
  operationsSupportEmail: string;
  technicalSupportEmail: string;
  firmInvoiceFromEmail: string;
  firmInvoiceBccEmail: string;
  settingsComponent: 'EmailConfigModal';
  healthCheckEndpoint: '/health/emailNotifications';
}

// Tenant Settings Module Configuration
export interface TenantSettingsModuleConfig extends ModuleConfig {
  contactInfo: ContactInfo;
  emailNotifications: {
    operationsSupportEmail: string;
    technicalSupportEmail: string;
    firmInvoiceFromEmail: string;
    firmInvoiceBccEmail: string;
  };
  domains: string[];
  settingsComponent: 'TenantSettingsModal';
  healthCheckEndpoint: '/health/tenantSettings';
}

// Reporting Module Configuration
export interface ReportingModuleConfig extends ModuleConfig {
  enabled: boolean;
  dataProcessingStartDate?: string; // ISO date string for backfill start date
  arExceptionReporting: {
    enabled: boolean;
  };
  trustReconciliationReporting?: {
    enabled: boolean;
    liabilityAccountId?: string; // QBO liability account for trust reconciliation
    trustAccounts: Array<{
      clioTrustAccountId: string;
      qboTrustAccountId: string;
      trustAccountName: string;
      trustAccountDescription?: string;
    }>;
  };
  matterLedgerReporting?: {
    enabled: boolean;
    pdfTemplateId?: string;
  };
  matterStatementReporting?: {
    enabled: boolean;
    pdfTemplateId?: string;
  };
  healthCheckEndpoint: '/health/reporting';
}

// Contact Information (updated to support both legacy and new array structures)
export interface ContactInfo {
  // Legacy single email format (for backward compatibility)
  adminEmail?: { address: string; validated: boolean };
  techEmail?: { address: string; validated: boolean };
  opsEmail?: { address: string; validated: boolean };
  juniperFromEmail?: { address: string; verified: boolean };
  juniperBccEmail?: { address: string; validated: boolean };
  // New array format
  admin?: { email: string }[];
  technicalSupport?: { email: string }[];
  operationalSupport?: { email: string }[];
}

// Enhanced Tenant Configuration
export interface EnhancedTenantConfig {
  tenantId: string;
  tenantName: string;
  alias: string;
  authorizedDomains: string[]; // NEW: Primary domain management
  allowedEmails: string[]; // Individual email overrides for tenant access
  // Legacy fields - deprecated but kept for backward compatibility
  domains?: string[];
  primaryDomain?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  contactInfo: ContactInfo;
  features: FeatureFlag[];
  modules: Record<string, ModuleConfig>;
}

// Update DTO
export interface UpdateTenantConfigDto {
  features?: FeatureFlag[];
  modules?: Record<string, ModuleConfig>;
  contactInfo?: Partial<ContactInfo>;
  tenantName?: string;
  alias?: string;
  authorizedDomains?: string[]; // NEW: Primary domain management
  allowedEmails?: string[]; // Individual email overrides for tenant access
  // Legacy fields - deprecated
  primaryDomain?: string;
}

// Integration Card Props
export interface IntegrationCardProps {
  feature: FeatureFlag;
  moduleConfig: ModuleConfig;
  onConfigure: (moduleName: string) => void;
  onHealthCheck?: (moduleName: string) => void;
}

// Module Health Status
export interface ModuleHealth {
  status: 'connected' | 'disconnected' | 'error' | 'checking';
  message?: string;
  lastChecked?: string;
  details?: Record<string, any>;
}

// Clio Webhook Models (from Clio API docs)
export interface ClioWebhookModel {
  name: string;
  displayName: string;
  events: string[];
  special?: boolean; // For Matter and Clio Payments
}

export interface ClioWebhookSubscription {
  modelName: string;
  events: string[];
  enabled: boolean;
  clioWebhookId?: string; // Clio's webhook ID from their API
  webhookUrl?: string; // Full webhook URL including model path
  status?: string; // active, inactive, pending, error
  createdAt?: string;
  updatedAt?: string;
  hmacSecretPath?: string;
}

// QBO Webhook Models
export interface QboWebhookModel {
  name: string;
  displayName: string;
  events: string[];
  special?: boolean;
}

export interface QboWebhookSubscription {
  modelName: string;
  events: string[];
  enabled: boolean;
  qboWebhookId?: string; // QBO's webhook ID from their API
  webhookUrl?: string; // Full webhook URL including model path
  status?: string; // active, inactive, pending, error
  createdAt?: string;
  updatedAt?: string;
  hmacSecretPath?: string;
}

// Integration Form Data (for modals)
export interface ClioFormData {
  appId: string;
  clientId: string;
  clientSecret: string;
  authorizationCode: string;
  accessToken?: string;
  refreshToken?: string;
  enableWebhooks: boolean;
  webhookSubscriptions: ClioWebhookSubscription[];
  // Remove webhook endpoint - it's always our backend
}

export interface QboFormData {
  appId: string;
  clientId: string;
  clientSecret: string;
  webhookSecret?: string;
  realmId: string;
  accessToken?: string;
  refreshToken?: string;
  enableWebhooks: boolean;
  webhookSubscriptions: QboWebhookSubscription[];
}

export interface EmailFormData {
  operationsSupportEmail: string;
  technicalSupportEmail: string;
  firmInvoiceFromEmail: string;
  firmInvoiceBccEmail: string;
}

// Modal Props
export interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  initialData?: any;
  isLoading?: boolean;
}

// Health Check Response
export interface HealthCheckResponse {
  [moduleName: string]: ModuleHealth;
}

// ======================
// Email Verification Types
// ======================

export interface EmailVerificationStatus {
  email: string;
  status: 'Success' | 'Pending' | 'Failed' | 'TemporaryFailure' | 'NotStarted';
  verificationToken?: string;
  lastVerifiedAt?: string;
  isVerifying?: boolean;
}

export interface EmailFieldVerificationState extends EmailVerificationStatus {
  fieldName: string;
}

// Extended EmailFormData with verification status
export interface EmailFormDataWithVerification extends EmailFormData {
  verificationStatus?: {
    operationsSupportEmail?: EmailVerificationStatus;
    technicalSupportEmail?: EmailVerificationStatus;
    firmInvoiceFromEmail?: EmailVerificationStatus;
    firmInvoiceBccEmail?: EmailVerificationStatus;
  };
}
